import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { decryptData } from "@/shared/utils/encryption";
import { serverEncryption, decryptLast4UserData } from "@/shared/utils/serverEncryption";
import { useAppContext } from "./useAppContext";

interface UseRegularSSNDataResult {
  ssn: string | null;
  last4: string | null;
  isLoading: boolean;
  error: string | null;
  hasSSN: boolean;
  refetch: () => void;
}

interface SSNQueryResult {
  ssn: string | null;
  last4: string | null;
}

interface UseRegularSSNDataOptions {
  skipDecrypt?: boolean;
}

export function useRegularSSNData(options: UseRegularSSNDataOptions = {}): UseRegularSSNDataResult {
  const { skipDecrypt = false } = options;
  const { user } = useAppContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["regularSSNData", user?.user_id, skipDecrypt],
    queryFn: async (): Promise<SSNQueryResult> => {
      if (!user?.user_id) {
        return { ssn: null, last4: null };
      }

      const { data: userData, error } = await supabase
        .from("users")
        .select("ssn_encrypted")
        .eq("user_id", user.user_id)
        .single();

      if (error || !userData?.ssn_encrypted) {
        return { ssn: null, last4: null };
      }

      if (skipDecrypt) {
        const last4 = await decryptLast4UserData(user.user_id, userData.ssn_encrypted, "ssn");
        return { ssn: null, last4: last4 || null };
      }

      try {
        const decryptedSSN = await serverEncryption.decrypt(
          userData.ssn_encrypted,
          "user",
          user.user_id,
          "ssn",
        );
        const digits = decryptedSSN.replace(/\D/g, "");
        const last4 = digits.length >= 4 ? digits.slice(-4) : null;
        return { ssn: decryptedSSN, last4 };
      } catch {
        try {
          const decryptedSSN = await decryptData(userData.ssn_encrypted);
          const digits = decryptedSSN.replace(/\D/g, "");
          const last4 = digits.length >= 4 ? digits.slice(-4) : null;
          return { ssn: decryptedSSN, last4 };
        } catch (oldError) {
          console.error("Both decryption methods failed:", oldError);
          throw new Error("Failed to decrypt user SSN");
        }
      }
    },
    enabled: !!user?.user_id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  return {
    ssn: data?.ssn ?? null,
    last4: data?.last4 ?? null,
    isLoading,
    error: error?.message || null,
    hasSSN: !!(data?.ssn || data?.last4),
    refetch,
  };
}
